import { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Copy, CheckCircle2, XCircle } from 'lucide-react';

const extractIndexes = (text) => {
  const out = new Set();
  const regex = /\{\{(\d+)\}\}/g;
  let m = regex.exec(String(text || ''));
  while (m) {
    out.add(Number(m[1]));
    m = regex.exec(String(text || ''));
  }
  return [...out].sort((a, b) => a - b);
};

export const PruebasTab = ({ initialWabaId = null } = {}) => {
  const [provider, setProvider] = useState('gupshup');
  const [numbers, setNumbers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [wabaId, setWabaId] = useState(initialWabaId || '');
  const [templateId, setTemplateId] = useState('');
  const [destination, setDestination] = useState('');
  const [paramsMap, setParamsMap] = useState({});
  const [sending, setSending] = useState(false);
  const [log, setLog] = useState(null);
  const [showTech, setShowTech] = useState(false);
  const [wabaDetail, setWabaDetail] = useState(null);

  useEffect(() => {
    if (!initialWabaId) {
      setWabaDetail(null);
      setNumbers([]);
      return;
    }
    setWabaId(initialWabaId);
    api.get(`/whatsapp/waba/${initialWabaId}/detail`)
      .then((r) => {
        const detail = r.data || null;
        setWabaDetail(detail);
        if (detail?.provider) setProvider(String(detail.provider).toLowerCase());
        if (detail?.provider === 'gupshup') {
          setNumbers([{
            id: detail.id,
            app_name: detail.name || 'Gupshup',
            source_phone: detail.phone_number || '',
          }]);
        } else {
          setNumbers([]);
        }
      })
      .catch(() => {
        setWabaDetail(null);
        setNumbers([]);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (provider === 'gupshup') {
      const activeWabaId = wabaId || numbers[0]?.id || '';
      if (!activeWabaId) {
        setTemplates([]);
        setTemplateId('');
        return;
      }
      if (!wabaId && activeWabaId) {
        setWabaId(activeWabaId);
        return;
      }
      api.get(`/whatsapp/gupshup/saved-templates/${activeWabaId}`).then((r) => {
        const list = Array.isArray(r.data?.templates) ? r.data.templates : [];
        setTemplates(list);
        setTemplateId((prev) => (list.some((tpl) => tpl.id === prev) ? prev : (list[0]?.id || '')));
      }).catch(() => {
        setTemplates([]);
        setTemplateId('');
      });
      return;
    }

    api.get('/whatsapp/templates/all', { params: { provider: 'twilio', page: 1, page_size: 200 } }).then((r) => {
      const list = Array.isArray(r.data?.items) ? r.data.items : [];
      setTemplates(list);
      setTemplateId((prev) => (list.some((tpl) => tpl.id === prev) ? prev : (list[0]?.id || '')));
    }).catch(() => {
      setTemplates([]);
      setTemplateId('');
    });
  }, [provider, wabaId, numbers]);

  useEffect(() => {
    setParamsMap({});
    setLog(null);
    setShowTech(false);
  }, [provider]);

  const selectedTemplate = useMemo(() => templates.find((t) => t.id === templateId) || null, [templates, templateId]);
  const indexes = useMemo(() => extractIndexes(selectedTemplate?.body || ''), [selectedTemplate]);

  useEffect(() => {
    if (!selectedTemplate) return;
    const byIndex = new Map((selectedTemplate.variable_mapping || []).map((m) => [Number(m.index), m]));
    const seed = {};
    indexes.forEach((idx) => {
      const row = byIndex.get(idx);
      seed[String(idx)] = row?.custom_value || '';
    });
    setParamsMap(seed);
  }, [selectedTemplate, indexes.join(',')]);

  const preview = useMemo(() => String(selectedTemplate?.body || '').replace(/\{\{(\d+)\}\}/g, (_, n) => paramsMap[n] || `{{${n}}}`), [selectedTemplate, paramsMap]);

  const send = async () => {
    if (!templateId || !destination || (provider === 'gupshup' && !wabaId)) {
      toast.error(provider === 'gupshup' ? 'Completa proveedor, número WABA, plantilla y destino' : 'Completa proveedor, plantilla y destino');
      return;
    }
    const params = indexes.map((i) => String(paramsMap[String(i)] || ''));
    setSending(true);
    try {
      const payload = provider === 'gupshup'
        ? {
          provider,
          config_id: wabaId,
          destination,
          template_id: templateId,
          params,
        }
        : {
          provider,
          destination,
          template_id: templateId,
          params,
        };

      const r = await api.post('/whatsapp/gupshup/test', payload, { skipAuthRedirect: true });
      const statusCode = Number(r.data?.gupshup_status_code || r.status || 0);
      setLog({
        at: new Date().toLocaleTimeString(),
        ...r.data,
        provider,
        status_code: statusCode,
        provider_response: r.data?.gupshup_response || {},
      });
      toast.success('Mensaje de prueba enviado');
    } catch (e) {
      const data = e.response?.data || {};
      const statusCode = Number(data.gupshup_status_code || e.response?.status || 0);
      setLog({
        at: new Date().toLocaleTimeString(),
        ...data,
        provider,
        status_code: statusCode,
        provider_response: data.gupshup_response || {},
      });
      setShowTech(true);
      toast.error(data.message || 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  };

  const copyText = async (text, ok) => {
    try { await navigator.clipboard.writeText(String(text || '')); toast.success(ok); } catch { toast.error('No se pudo copiar'); }
  };

  if (wabaDetail && wabaDetail.credentials_present === false) {
    return <Card className="bg-zinc-950/70 border-white/10" data-testid="whatsapp-pruebas-empty-card"><CardContent className="p-6 text-center text-zinc-400 text-sm">Este WABA no tiene credenciales configuradas todavía</CardContent></Card>;
  }
  if (!wabaDetail && provider === 'gupshup' && !numbers.length) {
    return <Card className="bg-zinc-950/70 border-white/10" data-testid="whatsapp-pruebas-empty-card"><CardContent className="p-6 text-center text-zinc-400 text-sm">Configura al menos un número WABA primero</CardContent></Card>;
  }

  const logStatusCode = Number(log?.status_code || log?.gupshup_status_code || 0);
  const logResponsePayload = log?.provider_response || log?.gupshup_response || {};
  const sendOk = logStatusCode === 200 || logStatusCode === 201 || logStatusCode === 202;

  return (
    <div className="grid xl:grid-cols-5 gap-4" data-testid="whatsapp-pruebas-root">
      <Card className="xl:col-span-3 bg-zinc-950/70 border-white/10" data-testid="whatsapp-pruebas-config-card">
        <CardHeader>
          <CardTitle className="text-white text-base">Enviar un WhatsApp de prueba</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Manda un mensaje real a tu propio celular para confirmar que tu número quedó bien conectado.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {wabaDetail ? (
            <div className="rounded-md border border-emerald-500/20 bg-emerald-500/5 px-2 py-2" data-testid="whatsapp-pruebas-waba-info">
              <p className="text-[11px] text-emerald-300">
                Origen: {wabaDetail.name || wabaDetail.provider?.toUpperCase()} · {wabaDetail.phone_number || '—'}
              </p>
            </div>
          ) : null}
          <div>
            <Label className="text-xs">Proveedor</Label>
            <select className="w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-xs" value={provider} onChange={(e) => setProvider(e.target.value)} disabled={!!wabaDetail} data-testid="whatsapp-pruebas-provider-select">
              <option value="gupshup">Gupshup</option>
              <option value="twilio">Twilio</option>
            </select>
          </div>
          {provider === 'gupshup' ? (
            <div><Label className="text-xs">Número WABA</Label><select className="w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-xs" value={wabaId} onChange={(e) => setWabaId(e.target.value)} data-testid="whatsapp-pruebas-waba-select">{numbers.map((n) => <option key={n.id} value={n.id}>{`${n.app_name} — ${n.source_phone}`}</option>)}</select></div>
          ) : null}
          <div><Label className="text-xs">Plantilla</Label><select className="w-full mt-1 bg-black/30 border border-white/10 rounded-md px-2 py-2 text-xs" value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="whatsapp-pruebas-template-select">{templates.map((t) => <option key={t.id} value={t.id}>{t.name || t.content_sid || t.template_id}</option>)}</select></div>
          {selectedTemplate && <div className="flex gap-2 flex-wrap" data-testid="whatsapp-pruebas-template-badges"><Badge className={provider === 'twilio' ? 'bg-blue-500/20 text-blue-300 border-0' : 'bg-emerald-500/20 text-emerald-300 border-0'}>{provider.toUpperCase()}</Badge><Badge className="bg-emerald-500/20 text-emerald-300 border-0">{selectedTemplate.status}</Badge><Badge className="bg-cyan-500/20 text-cyan-300 border-0">{selectedTemplate.category}</Badge><Badge className="bg-zinc-700 text-zinc-200 border-0">{indexes.length} variables</Badge></div>}
          {indexes.map((idx) => (
            <div key={idx}><Label className="text-xs">{`{{${idx}}}`}</Label><Input value={paramsMap[String(idx)] || ''} onChange={(e) => setParamsMap((p) => ({ ...p, [String(idx)]: e.target.value }))} data-testid={`whatsapp-pruebas-param-input-${idx}`} /></div>
          ))}
          <div><Label className="text-xs">Número destino (tu celular)</Label><Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder={provider === 'twilio' ? '+5215520952500' : '5215520952500'} data-testid="whatsapp-pruebas-destination-input" /></div>
          <Button className="w-full bg-emerald-600 hover:bg-emerald-500" onClick={send} disabled={sending} data-testid="whatsapp-pruebas-send-button">{sending ? 'Enviando...' : 'Enviar mensaje de prueba'}</Button>
        </CardContent>
      </Card>

      <Card className="xl:col-span-2 bg-zinc-950/70 border-white/10" data-testid="whatsapp-pruebas-preview-card">
        <CardHeader><CardTitle className="text-white text-base">Vista previa y resultado</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-xl p-3 bg-[#ece5dd] border border-yellow-900/20" data-testid="whatsapp-pruebas-live-preview"><p className="text-[12px] text-zinc-900 whitespace-pre-wrap">{preview || 'Selecciona plantilla para previsualizar'}</p></div>
          <p className="text-xs text-zinc-400" data-testid="whatsapp-pruebas-destination-preview">Enviando a: {destination || '—'}</p>

          {log && (
            <div className="space-y-2" data-testid="whatsapp-pruebas-log-box">
              <div className={`rounded-lg border p-3 ${sendOk ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-red-500/30 bg-red-500/10'}`} data-testid="whatsapp-pruebas-result-banner">
                <p className={`text-sm font-semibold flex items-center gap-1.5 ${sendOk ? 'text-emerald-300' : 'text-red-300'}`} data-testid="whatsapp-pruebas-result-title">
                  {sendOk ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {sendOk ? 'Mensaje de prueba enviado' : 'No se pudo enviar'}
                </p>
                <p className="text-[11px] text-zinc-300 mt-1 leading-relaxed">
                  {sendOk
                    ? `Enviado a ${destination || 'tu número'} a las ${log.at}. Si no te llega en unos segundos, revisa que ese número esté registrado en WhatsApp y que la plantilla esté aprobada.`
                    : `${log.message || 'El proveedor rechazó el envío.'} — ${log.at}`}
                </p>
              </div>

              <details className="rounded-md border border-white/10 bg-black/20 p-2" data-testid="whatsapp-pruebas-tech-details" open={showTech}>
                <summary className="text-[11px] text-zinc-400 cursor-pointer select-none">Detalles técnicos (para soporte)</summary>
                <div className="mt-2 space-y-2">
                  <div>
                    <p className="text-[10px] text-zinc-500 mb-1">Petición (cURL)</p>
                    <pre className="text-[11px] bg-black/40 text-zinc-200 border border-white/10 rounded-md p-2 overflow-x-auto" data-testid="whatsapp-pruebas-log-request-pre">{log.curl_equivalent || '-'}</pre>
                    <Button size="sm" variant="outline" onClick={() => copyText(log.curl_equivalent, 'cURL copiado')} data-testid="whatsapp-pruebas-copy-request"><Copy className="w-3.5 h-3.5 mr-1" />Copiar cURL</Button>
                  </div>
                  <div>
                    <p className="text-[10px] text-zinc-500 mb-1">Respuesta del proveedor (código {logStatusCode || 'N/A'})</p>
                    <pre className="text-[11px] bg-black/40 text-zinc-200 border border-white/10 rounded-md p-2 overflow-x-auto" data-testid="whatsapp-pruebas-log-response-pre">{JSON.stringify(logResponsePayload, null, 2)}</pre>
                    <Button size="sm" variant="outline" onClick={() => copyText(JSON.stringify(logResponsePayload, null, 2), 'Respuesta copiada')} data-testid="whatsapp-pruebas-copy-response"><Copy className="w-3.5 h-3.5 mr-1" />Copiar respuesta</Button>
                  </div>
                </div>
              </details>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
